import * as _ from 'lodash';
import { DateTime } from 'luxon';
import {
  CompensationRange,
  ProjectedOperationalCostLog,
  ProjectedRevenuePayCostLog,
  BillingType,
  ExtOperStatus,
} from '../types';

export const iconColor = {
  defaultPayRateIconColor: 0,
  aboveRangeIconColor: 1,
  belowRangeIconColor: 2,
};

export const extOperStatusToBoolean = (status?: ExtOperStatus) => {
  if (!status) {
    return undefined;
  }

  if (status === ExtOperStatus.SUCCESS) {
    return true;
  }

  return false;
};

export const payRateColor = ({
  compensationRate,
  compensationRange,
}: {
  compensationRate?: number | null;
  compensationRange?: CompensationRange;
}) => {
  if (!compensationRate) {
    compensationRate = 0;
  }

  if (!compensationRange?.min || !compensationRange?.max) {
    return iconColor.defaultPayRateIconColor;
  }

  if (compensationRate > compensationRange.max) {
    return iconColor.aboveRangeIconColor;
  }
  if (compensationRate < compensationRange.min) {
    return iconColor.belowRangeIconColor;
  }

  return iconColor.defaultPayRateIconColor;
};

export const projectedRevenueLogsToText = (
  projectedRevenueLogs: ProjectedRevenuePayCostLog[],
  revenue: number,
) => {
  const logsText = projectedRevenueLogs.map((log, index) => {
    const getTypePrompt = (
      log: ProjectedRevenuePayCostLog,
      logsLength: number,
    ) => {
      if (log.type === 'FROM_INVOICE_DATE_TO_NOW') {
        return 'From Invoice Date to Now';
      }
      if (log.type === 'FROM_HEADCOUNT_CHANGE_DATE_TO_HEADCOUNT_CHANGE_DATE') {
        return `From ${
          index === 1 ? 'Now' : 'HC Change Date'
        } to HC Change Date`;
      }

      return `From ${logsLength === 2 ? 'Now' : 'last HC Change Date'} to ${
        log.toDate !== DateTime.now().endOf('year').toFormat('yyyy-LL-dd')
          ? 'End date'
          : 'End of Year'
      }`;
    };
    const typePrompt = getTypePrompt(log, projectedRevenueLogs.length);

    return `${typePrompt}: ${log.fromDate} - ${log.toDate}: $${_.round(
      log.blendedBillRate,
      2,
    )} * 8 * ${log.headcount} people * ${log.days} days`;
  });

  logsText.push(
    `Total: $${_.round(
      projectedRevenueLogs.reduce(
        (acc, log) => acc + log.blendedBillRate * 8 * log.headcount * log.days,
        0,
      ) + revenue,
      2,
    )}`,
  );

  return logsText.join('\n');
};

export const getTypePrompt = (
  log: ProjectedRevenuePayCostLog | ProjectedOperationalCostLog,
  index: number,
  logs: ProjectedRevenuePayCostLog[] | ProjectedOperationalCostLog[],
) => {
  if (log.type === 'FROM_INVOICE_DATE_TO_NOW') {
    return 'From Invoice Date to Now';
  }
  if (log.type === 'FROM_HEADCOUNT_CHANGE_DATE_TO_HEADCOUNT_CHANGE_DATE') {
    return `From ${index === 1 ? 'Now' : 'HC Change Date'} to HC Change Date`;
  }

  return `From ${logs?.length === 2 ? 'Now' : 'last HC Change Date'} to ${
    log.toDate !== DateTime.now().endOf('year').toFormat('yyyy-LL-dd')
      ? 'End date'
      : 'End of Year'
  }`;
};

export const getProjectedRevenueLogsAmount = (
  index: number,
  logs: ProjectedRevenuePayCostLog[] | ProjectedOperationalCostLog[],
  type: 'blendedBillRate' | 'operationalCost' | 'blendedPayRate',
) => {
  if (!logs) return;
  const log: any = logs[index];

  return log[type] * 8 * log.headcount * log.days;
};

export const getBillingType = (billingType: BillingType) => {
  if (billingType === 'once') {
    return 'Once';
  }
  if (billingType === 'twice') {
    return 'Twice';
  }
  return 'Fixed';
};
